(function () {
  'use strict';

  var elementName = 'cusprint-plugin-carousel';

  if (!window.customElements || window.customElements.get(elementName)) return;

  var selectedBlockBySection = new Map();

  function isShopifyDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function getEditorEventIds(event) {
    var detail = event && event.detail ? event.detail : {};

    return {
      sectionId: detail.sectionId == null ? '' : String(detail.sectionId),
      blockId: detail.blockId == null ? '' : String(detail.blockId)
    };
  }

  document.addEventListener('shopify:block:select', function (event) {
    if (!isShopifyDesignMode()) return;

    var ids = getEditorEventIds(event);
    if (!ids.sectionId || !ids.blockId) return;
    selectedBlockBySection.clear();
    selectedBlockBySection.set(ids.sectionId, ids.blockId);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    if (!isShopifyDesignMode()) return;

    var ids = getEditorEventIds(event);
    if (!ids.sectionId || !ids.blockId) return;
    if (selectedBlockBySection.get(ids.sectionId) === ids.blockId) {
      selectedBlockBySection.delete(ids.sectionId);
    }
  });

  class CusprintPluginCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.slides = Array.from(this.querySelectorAll('[data-cpc-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-cpc-dot]'));
      this.viewport = this.querySelector('[data-cpc-viewport]');
      this.track = this.querySelector('[data-cpc-track]');
      this.previousButton = this.querySelector('[data-cpc-previous]');
      this.nextButton = this.querySelector('[data-cpc-next]');
      this.autoplayButton = this.querySelector('[data-cpc-autoplay-toggle]');
      this.autoplayLabel = this.querySelector('[data-cpc-autoplay-label]');
      this.status = this.querySelector('[data-cpc-status]');
      this.pauseReasons = new Set();
      this.autoplayTimer = null;
      this.focusTimer = null;
      this.heightFrame = null;
      this.resizeObserver = null;
      this.touchStart = null;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.autoplayDelay = Math.max(
        Number.parseInt(this.dataset.autoplayDelay, 10) || 6000,
        1000
      );
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.sectionId = this.dataset.sectionId || '';
      this.designMode = isShopifyDesignMode();

      var initiallyActive = this.slides.findIndex(function (slide) {
        return slide.getAttribute('aria-hidden') === 'false';
      });

      if (this.designMode && this.sectionId) {
        var rememberedBlockId = selectedBlockBySection.get(this.sectionId);
        var rememberedIndex = this.slides.findIndex(function (slide) {
          return slide.dataset.cpcBlockId === rememberedBlockId;
        });

        if (rememberedIndex >= 0) {
          initiallyActive = rememberedIndex;
          this.pauseReasons.add('editor');
        }
      }

      this.currentIndex = initiallyActive >= 0 ? initiallyActive : 0;

      this.bindEvents();
      this.observeSlideSizes();
      this.updateAutoplayControl();
      this.render(false);
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.clearAutoplay();
      window.clearTimeout(this.focusTimer);
      window.cancelAnimationFrame(this.heightFrame);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      if (this.motionQuery && this.handleMotionChange) {
        if (typeof this.motionQuery.removeEventListener === 'function') {
          this.motionQuery.removeEventListener('change', this.handleMotionChange);
        } else if (typeof this.motionQuery.removeListener === 'function') {
          this.motionQuery.removeListener(this.handleMotionChange);
        }
      }

      if (this.mobileQuery && this.handleBreakpointChange) {
        if (typeof this.mobileQuery.removeEventListener === 'function') {
          this.mobileQuery.removeEventListener('change', this.handleBreakpointChange);
        } else if (typeof this.mobileQuery.removeListener === 'function') {
          this.mobileQuery.removeListener(this.handleBreakpointChange);
        }
      }

      if (this.viewport) {
        this.viewport.style.removeProperty('height');
      }

      this.initialized = false;
    }

    bindEvents() {
      var signal = this.abortController.signal;

      if (this.previousButton) {
        this.previousButton.addEventListener(
          'click',
          () => this.goTo(this.currentIndex - 1, true),
          { signal: signal }
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          () => this.goTo(this.currentIndex + 1, true),
          { signal: signal }
        );
      }

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => this.goTo(Number.parseInt(dot.dataset.slideIndex, 10), true),
          { signal: signal }
        );
      });

      if (this.autoplayButton) {
        this.autoplayButton.addEventListener(
          'click',
          () => this.toggleManualAutoplay(),
          { signal: signal }
        );
      }

      this.addEventListener(
        'keydown',
        (event) => {
          if (
            event.defaultPrevented ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey
          ) {
            return;
          }

          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goTo(this.currentIndex - 1, true);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goTo(this.currentIndex + 1, true);
          }
        },
        { signal: signal }
      );

      if (this.pauseOnHover) {
        this.addEventListener(
          'pointerenter',
          () => this.pause('hover'),
          { signal: signal }
        );
        this.addEventListener(
          'pointerleave',
          () => this.resume('hover'),
          { signal: signal }
        );
      }

      this.addEventListener(
        'focusin',
        () => this.pause('focus'),
        { signal: signal }
      );
      this.addEventListener(
        'focusout',
        () => {
          window.clearTimeout(this.focusTimer);
          this.focusTimer = window.setTimeout(() => {
            if (!this.contains(document.activeElement)) {
              this.resume('focus');
            }
          }, 0);
        },
        { signal: signal }
      );

      this.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) return;
          this.touchStart = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY
          };
          this.pause('touch');
        },
        { passive: true, signal: signal }
      );

      this.addEventListener(
        'touchend',
        (event) => {
          var start = this.touchStart;
          var touch = event.changedTouches[0];
          this.touchStart = null;
          this.resume('touch', false);

          if (!start || !touch) {
            this.syncAutoplay();
            return;
          }

          var deltaX = start.x - touch.clientX;
          var deltaY = start.y - touch.clientY;

          if (Math.abs(deltaX) >= 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
            this.goTo(this.currentIndex + (deltaX > 0 ? 1 : -1), true);
          } else {
            this.syncAutoplay();
          }
        },
        { passive: true, signal: signal }
      );

      this.addEventListener(
        'touchcancel',
        () => {
          this.touchStart = null;
          this.resume('touch');
        },
        { passive: true, signal: signal }
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.pause('visibility');
          } else {
            this.resume('visibility');
          }
        },
        { signal: signal }
      );

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!this.designMode || !this.sectionId) return;

          var ids = getEditorEventIds(event);
          if (!ids.sectionId || !ids.blockId) return;
          if (ids.sectionId !== this.sectionId) {
            this.resume('editor');
            return;
          }

          var selectedIndex = this.slides.findIndex(function (slide) {
            return slide.dataset.cpcBlockId === ids.blockId;
          });
          if (selectedIndex < 0) {
            this.resume('editor');
            return;
          }

          this.pause('editor');
          this.goTo(selectedIndex, false);
        },
        { signal: signal }
      );

      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          if (!this.designMode || !this.sectionId) return;

          var ids = getEditorEventIds(event);
          if (ids.sectionId !== this.sectionId || !ids.blockId) return;

          var belongsToThisCarousel = this.slides.some(function (slide) {
            return slide.dataset.cpcBlockId === ids.blockId;
          });
          if (!belongsToThisCarousel) return;

          this.resume('editor');
        },
        { signal: signal }
      );

      window.addEventListener(
        'resize',
        () => this.queueViewportHeightSync(),
        { signal: signal }
      );

      this.querySelectorAll('img').forEach((image) => {
        image.addEventListener(
          'load',
          () => this.queueViewportHeightSync(),
          { signal: signal }
        );
      });

      this.handleMotionChange = () => {
        this.queueViewportHeightSync();
        this.syncAutoplay();
      };
      if (typeof this.motionQuery.addEventListener === 'function') {
        this.motionQuery.addEventListener('change', this.handleMotionChange);
      } else if (typeof this.motionQuery.addListener === 'function') {
        this.motionQuery.addListener(this.handleMotionChange);
      }

      this.handleBreakpointChange = () => this.queueViewportHeightSync();
      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
      } else if (typeof this.mobileQuery.addListener === 'function') {
        this.mobileQuery.addListener(this.handleBreakpointChange);
      }
    }

    observeSlideSizes() {
      if (typeof window.ResizeObserver !== 'function') return;

      this.resizeObserver = new window.ResizeObserver(() => {
        this.queueViewportHeightSync();
      });

      this.slides.forEach((slide) => {
        this.resizeObserver.observe(slide);
      });
    }

    goTo(index, userInitiated) {
      if (!this.slides.length || !Number.isFinite(index)) return;

      var slideCount = this.slides.length;
      this.currentIndex = (index % slideCount + slideCount) % slideCount;
      this.render(Boolean(userInitiated));

      if (userInitiated) {
        this.clearAutoplay();
      }
      this.syncAutoplay();
    }

    render(announce) {
      if (!this.track || !this.slides.length) return;

      this.track.style.transform =
        'translate3d(-' + String(this.currentIndex * 100) + '%, 0, 0)';

      this.slides.forEach((slide, index) => {
        var isActive = index === this.currentIndex;
        slide.setAttribute('aria-hidden', String(!isActive));
        if (isActive) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });

      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === this.currentIndex));
      });

      if (announce && this.status) {
        this.status.textContent =
          'Slide ' + String(this.currentIndex + 1) + ' of ' + String(this.slides.length);
      }

      this.queueViewportHeightSync();
    }

    queueViewportHeightSync() {
      if (!this.viewport) return;

      window.cancelAnimationFrame(this.heightFrame);
      this.heightFrame = window.requestAnimationFrame(() => {
        this.heightFrame = null;
        this.syncViewportHeight();
      });
    }

    syncViewportHeight() {
      if (!this.viewport || !this.mobileQuery) return;

      if (!this.mobileQuery.matches) {
        this.viewport.style.removeProperty('height');
        return;
      }

      var activeSlide = this.slides[this.currentIndex];
      if (!activeSlide) return;

      var activeHeight = Math.ceil(activeSlide.getBoundingClientRect().height);
      if (activeHeight > 0) {
        this.viewport.style.height = String(activeHeight) + 'px';
      }
    }

    toggleManualAutoplay() {
      if (this.pauseReasons.has('manual')) {
        this.pauseReasons.delete('manual');
      } else {
        this.pauseReasons.add('manual');
      }

      this.updateAutoplayControl();
      this.syncAutoplay();
    }

    updateAutoplayControl() {
      if (!this.autoplayButton || !this.autoplayLabel) return;

      var autoplayEnabledByUser = !this.pauseReasons.has('manual');
      var actionLabel = autoplayEnabledByUser
        ? 'Pause autoplay'
        : 'Resume autoplay';
      this.autoplayButton.setAttribute('aria-pressed', String(autoplayEnabledByUser));
      this.autoplayButton.setAttribute('title', actionLabel);
      this.autoplayLabel.textContent = actionLabel;
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      if (reason === 'manual') {
        this.updateAutoplayControl();
      }
      this.clearAutoplay();
    }

    resume(reason, shouldSync) {
      this.pauseReasons.delete(reason);
      if (reason === 'manual') {
        this.updateAutoplayControl();
      }
      if (shouldSync !== false) {
        this.syncAutoplay();
      }
    }

    syncAutoplay() {
      this.clearAutoplay();

      if (
        !this.autoplayEnabled ||
        this.slides.length < 2 ||
        this.pauseReasons.size > 0 ||
        document.hidden ||
        this.motionQuery.matches
      ) {
        return;
      }

      this.autoplayTimer = window.setTimeout(() => {
        this.goTo(this.currentIndex + 1, false);
      }, this.autoplayDelay);
    }

    clearAutoplay() {
      if (this.autoplayTimer === null) return;
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  window.customElements.define(elementName, CusprintPluginCarousel);
})();
